import { DEFAULT_STREAK_FILTERS } from '@bo-constants/filters';
import type { DateRange } from '@library/types/dates';
import { GlobalFilters } from '@library/components/GlobalFilters';
import { Loading } from '@library/components/Loading';
import { PageTitle } from '@library/components/PageTitle';
import { StreakBoard } from '@bo-components/streak/StreakBoard';
import { StreakUpcomingProjections } from '@bo-components/streak/StreakUpcomingProjections';
import { useDashBoard } from '@bo-hooks/useDashboard';
import { useDebounce } from '@library/hooks/useDebounce';
import { useMemo } from 'react';
import { useSentrySetDashBoardTag } from '@bo-hooks/sentry/useSentrySetDashBoardTag';
import { useStreakProjections } from '@bo-hooks/streaks/useStreakProjections';
import { withProfiler } from '@sentry/react';

export const StreakDashboardView = () => {
  useSentrySetDashBoardTag({ dashboardName: 'Streak' });

  const {
    durations,
    handleSearch,
    handleSelectDateRange,
    handleSelectLeague,
    isLeagueLoading,
    leagueInfo,
    leaguesList,
    searchValue,
    selectedLeagueId,
    selectedStartDate: selectedDate,
    setFilters
  } = useDashBoard({
    dashboardName: 'STREAK'
  });

  const handleReset = () => {
    setFilters(DEFAULT_STREAK_FILTERS);
    handleSearch('');
  };

  const handleChangeDate = (value: DateRange) => {
    handleSelectDateRange(value);
    handleReset();
  };

  const handleChangeLeague = (newLeagueId: string) => {
    handleSelectLeague(newLeagueId);
    handleReset();
  };

  const debouncedTextSearch = useDebounce({
    value: searchValue,
    delay: 500
  });

  const selectedDateString = useMemo(
    () => selectedDate.toFormat('yyyy-MM-dd'),
    [selectedDate]
  );

  const {
    unrankedProjectionsToDisplay: unrankedProjections,
    unrankedProjectionsLoading,
    rankedProjectionsToDisplay: rankedProjections,
    rankedProjectionsLoading,
    moveProjectionToRanked,
    moveProjectionToUnranked,
    setRankedProjections
  } = useStreakProjections({
    selectedDateString,
    selectedLeagueId: leagueInfo?.id,
    debouncedTextSearch
  });

  return (
    <div className="h-full">
      <section className="border-soFresh-140 w-auto px-6 pb-2 pt-3">
        <PageTitle title="Streak Board" />
        <div className="pb-4">
          <div className="flex flex-col gap-4 py-4">
            {isLeagueLoading ? (
              <Loading loadingMessage="Loading Filters" />
            ) : (
              <GlobalFilters
                durations={durations}
                dateRange={false}
                handleSearch={handleSearch}
                handleSelectDateRange={handleChangeDate}
                handleSelectLeague={handleChangeLeague}
                leaguesList={leaguesList}
                searchDisabled={false}
                searchValue={searchValue}
                searchPlaceholder="Search players or teams"
                selectedStartDate={selectedDate}
                selectedEndDate={selectedDate}
                selectedLeagueId={selectedLeagueId}
              />
            )}
          </div>
        </div>
      </section>
      <div className="flex size-full gap-3 px-6">
        <StreakUpcomingProjections
          unrankedProjections={unrankedProjections}
          unrankedProjectionsLoading={unrankedProjectionsLoading}
          onRankProjection={moveProjectionToRanked}
        />
        <StreakBoard
          selectedDate={selectedDate}
          rankedProjections={rankedProjections}
          rankedProjectionsLoading={rankedProjectionsLoading}
          onUnrankProjection={moveProjectionToUnranked}
          setRankedProjections={setRankedProjections}
        />
      </div>
    </div>
  );
};

export const StreakDashboard = withProfiler(StreakDashboardView);
